import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Homepage.css';
import Sidebar from '../components/Sidebar';
import ChallengeList from '../components/ChallengeList';

const Dashboard = () => {
  const [darkMode, setDarkMode] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    beginner: false,
    medium: false,
    difficult: false
  });
  
  // New unified search fields state - all selected by default
  const [searchFields, setSearchFields] = useState({
    authors: true,
    answers: true,
    questions: true,
    championship: true,
    date: false // Changed to false by default since we have date range
  });

  // Add search mode state
  const [searchMode, setSearchMode] = useState('all'); // 'all' or 'any'
  
  // Add date range state
  const [dateRange, setDateRange] = useState({
    min: 1990,
    max: 2025,
    minMonth: 1,
    maxMonth: 12,
    enabled: false
  });
  const [selectedDateRange, setSelectedDateRange] = useState({
    min: 1990,
    max: 2025,
    minMonth: 1,
    maxMonth: 12
  });
  
  const [uniqueAuthors, setUniqueAuthors] = useState([]);
  const [sortOption, setSortOption] = useState('title-asc');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showSearchFieldsDropdown, setShowSearchFieldsDropdown] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Handle data loaded from ChallengeList
  const handleDataLoad = (authors) => {
    setUniqueAuthors(authors);
  };

  const handleFilterChange = (filter) => {
    setFilters({
      ...filters,
      [filter]: !filters[filter]
    });
  };

  // New function to handle search field selection
  const handleSearchFieldToggle = (field) => {
    setSearchFields({
      ...searchFields,
      [field]: !searchFields[field]
    });
  };

  // Function to select/deselect all search fields
  const toggleAllSearchFields = (selectAll) => {
    setSearchFields({
      authors: selectAll,
      answers: selectAll,
      questions: selectAll,
      championship: selectAll,
      date: selectAll
    });
  };

  // Handle date range changes
  const handleDateRangeChange = (type, value) => {
    const numValue = parseInt(value);
    const newRange = { ...selectedDateRange, [type]: numValue };
    
    // Validate and adjust if needed
    if (type === 'min' && numValue > newRange.max) {
      newRange.max = numValue;
      newRange.minMonth = 1;
      newRange.maxMonth = 12;
    } else if (type === 'max' && numValue < newRange.min) {
      newRange.min = numValue;
      newRange.minMonth = 1;
      newRange.maxMonth = 12;
    } else if (type === 'min' && numValue === newRange.max) {
      // Same year - ensure month range is valid
      if (newRange.minMonth > newRange.maxMonth) {
        newRange.maxMonth = newRange.minMonth;
      }
    } else if (type === 'minMonth' && newRange.min === newRange.max) {
      // Same year - ensure min month not greater than max month
      if (numValue > newRange.maxMonth) {
        newRange.maxMonth = numValue;
      }
    } else if (type === 'maxMonth' && newRange.min === newRange.max) {
      // Same year - ensure max month not less than min month
      if (numValue < newRange.minMonth) {
        newRange.minMonth = numValue;
      }
    }
    
    setSelectedDateRange(newRange);
  };

  const handleDateRangeToggle = () => {
    setDateRange({
      ...dateRange,
      enabled: !dateRange.enabled
    });
  };

  const handleSortChange = (option) => {
    setSortOption(option);
    setShowSortDropdown(false);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    if (!sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.sort-dropdown-container') && !event.target.closest('.sort-button')) {
        setShowSortDropdown(false);
      }
      if (!event.target.closest('.search-fields-dropdown-container') && !event.target.closest('.search-fields-button')) {
        setShowSearchFieldsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  
  // Helper function to format month with leading zero
  const formatMonth = (monthNum) => {
    return monthNum.toString().padStart(2, '0');
  };

  const handleOverlayClick = () => {
    setSidebarOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Count selected search fields for button display
  const selectedFieldsCount = Object.values(searchFields).filter(Boolean).length;
  const totalFieldsCount = Object.keys(searchFields).length;
  const allFieldsSelected = selectedFieldsCount === totalFieldsCount;

  // Check if any non-default options are active
  const hasActiveOptions = !allFieldsSelected || searchMode !== 'all' || dateRange.enabled;

  return (
    <div className={`app-container ${!darkMode ? 'light-mode' : ''}`}>
      <button className="burger-menu" onClick={toggleSidebar}>
        <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
          <path d="M3 12h18M3 6h18M3 18h18"></path>
        </svg>
      </button>
      
      {sidebarOpen && (
        <div className="sidebar-overlay visible" onClick={handleOverlayClick}></div>
      )}
      
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <button className="sidebar-close" onClick={toggleSidebar} aria-label="Close sidebar">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
            <path d="M18 6L6 18M6 6l12 12"></path>
          </svg>
        </button>
        <div className="sidebar-content-wrapper">
          <Sidebar darkMode={darkMode} setDarkMode={setDarkMode} />
        </div>
      </div>
      
      <div className={`main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="filter-section">
          <div className="dashboard-header">
            <h1>"რა? სად? როდის?" შეკითხვების ბაზა</h1>
          </div>
          
          {/* Enhanced search container */}
          <div className="search-container mobile-search">
            <input 
              type="text" 
              placeholder="კითხვების ძებნა..." 
              className="search-input"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <div className="search-icon">
              {searchQuery ? (
                <button className="clear-search" onClick={clearSearch}>
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                  </svg>
                </button>
              ) : (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path>
                </svg>
              )}
            </div>
          </div>
          
          <div className="filter-row">
            {/* Enhanced search fields dropdown with search mode and date range */}
            <div className="dropdown-container">
              <button 
                className={`filter-button search-fields-button ${hasActiveOptions ? 'active' : ''}`}
                onClick={() => setShowSearchFieldsDropdown(!showSearchFieldsDropdown)}
              >
                ძებნის ოფციები {hasActiveOptions && 
                  <span className="search-options-indicator">
                    ({searchMode === 'all' ? 'ყველა' : 'ნებისმიერი'}{dateRange.enabled ? `, ${formatMonth(selectedDateRange.minMonth)}/${selectedDateRange.min} - ${formatMonth(selectedDateRange.maxMonth)}/${selectedDateRange.max}` : ''})
                  </span>
                }
                <svg className="dropdown-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
                </svg>
              </button>
              {showSearchFieldsDropdown && (
                <div className="dropdown-menu search-fields-dropdown-container">
                  {/* Search Mode Section */}
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">ძებნა</div>
                    <div className="search-mode-options">
                      <label className="radio-label">
                        <input 
                          type="radio" 
                          name="searchMode"
                          value="all"
                          checked={searchMode === 'all'}
                          onChange={(e) => setSearchMode(e.target.value)}
                        />
                        <span className="radio-text">
                          <span className="radio-title">ყველა სიტყვა</span>
                          <span className="radio-description">უნდა შეიცავდეს ყველა სიტყვას</span>
                        </span>
                      </label>
                      <label className="radio-label">
                        <input 
                          type="radio" 
                          name="searchMode"
                          value="any"
                          checked={searchMode === 'any'}
                          onChange={(e) => setSearchMode(e.target.value)}
                        />
                        <span className="radio-text">
                          <span className="radio-title">ნებისმიერი სიტყვა</span>
                          <span className="radio-description">უნდა შეიცავდეს ნებისმიერ სიტყვას</span>
                        </span>
                      </label>
                    </div>
                  </div>
                  
                  <div className="dropdown-divider"></div>
                  
                  {/* Date Range Section */}
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">თარიღით ძებნა</div>
                    <div className="date-range-container">
                      <div className="date-range-toggle">
                        <label className="checkbox-label">
                          <input 
                            type="checkbox" 
                            checked={dateRange.enabled}
                            onChange={handleDateRangeToggle}
                          />
                          თარიღით ძებნის ჩართვა
                        </label>
                      </div>
                      
                      {dateRange.enabled && (
                        <div className="date-range-controls">
                          <div className="range-slider-container">
                            <div className="date-range-display">
                              <span className="date-display-label">დან:</span>
                              <span className="date-display-value">
                                {formatMonth(selectedDateRange.minMonth)}/{selectedDateRange.min}
                              </span>
                              <span className="date-display-label">მდე:</span>
                              <span className="date-display-value">
                                {formatMonth(selectedDateRange.maxMonth)}/{selectedDateRange.max}
                              </span>
                            </div>
                            
                            <div className="year-range-section">
                              <div className="range-labels">
                                <span className="range-value">{selectedDateRange.min}</span>
                                <span className="range-label">წლის დიაპაზონი</span>
                                <span className="range-value">{selectedDateRange.max}</span>
                              </div>
                              
                              <div className="dual-range-slider">
                                <input
                                  type="range"
                                  min="1990"
                                  max="2025"
                                  value={selectedDateRange.min}
                                  onChange={(e) => handleDateRangeChange('min', e.target.value)}
                                  className="range-slider range-slider-min"
                                />
                                <input
                                  type="range"
                                  min="1990"
                                  max="2025"
                                  value={selectedDateRange.max}
                                  onChange={(e) => handleDateRangeChange('max', e.target.value)}
                                  className="range-slider range-slider-max"
                                />
                                <div className="slider-track"></div>
                                <div 
                                  className="slider-range"
                                  style={{
                                    left: `${((selectedDateRange.min - 1990) / 35) * 100}%`,
                                    right: `${100 - ((selectedDateRange.max - 1990) / 35) * 100}%`
                                  }}
                                ></div>
                              </div>
                            </div>
                            
                            <div className="month-selectors">
                              <div className="month-selector-group">
                                <label className="month-label">თვე დან:</label>
                                <select 
                                  value={selectedDateRange.minMonth}
                                  onChange={(e) => handleDateRangeChange('minMonth', e.target.value)}
                                  className="month-select"
                                >
                                  {[...Array(12)].map((_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                      {formatMonth(i + 1)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              
                              <div className="month-selector-group">
                                <label className="month-label">თვე მდე:</label>
                                <select 
                                  value={selectedDateRange.maxMonth}
                                  onChange={(e) => handleDateRangeChange('maxMonth', e.target.value)}
                                  className="month-select"
                                >
                                  {[...Array(12)].map((_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                      {formatMonth(i + 1)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="dropdown-divider"></div>
                  
                  {/* Search Fields Section */}
                  <div className="dropdown-section">
                    <div className="dropdown-section-title">ძებნა</div>
                    
                    {/* Select All / Deselect All options */}
                    <div className="dropdown-item dropdown-control">
                      <button 
                        className="control-button"
                        onClick={() => toggleAllSearchFields(true)}
                      >
                        ყველას მონიშვნა
                      </button>
                      <button 
                        className="control-button"
                        onClick={() => toggleAllSearchFields(false)}
                      >
                        ყველას გათიშვა
                      </button>
                    </div>
                    
                    {/* Individual search field options */}
                    <div className="dropdown-item">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={searchFields.questions}
                          onChange={() => handleSearchFieldToggle('questions')}
                        />
                        კითხვები
                      </label>
                    </div>
                    <div className="dropdown-item">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={searchFields.answers}
                          onChange={() => handleSearchFieldToggle('answers')}
                        />
                        პასუხები
                      </label>
                    </div>
                    <div className="dropdown-item">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={searchFields.authors}
                          onChange={() => handleSearchFieldToggle('authors')}
                        />
                        ავტორები
                      </label>
                    </div>
                    <div className="dropdown-item">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={searchFields.championship}
                          onChange={() => handleSearchFieldToggle('championship')}
                        />
                        პაკეტი/ტურნირი
                      </label>
                    </div>
                    <div className="dropdown-item">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={searchFields.date}
                          onChange={() => handleSearchFieldToggle('date')}
                        />
                        თარიღის ტექსტური ძებნა
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="search-container desktop-search">
              <input 
                type="text" 
                placeholder="კითხვების ძებნა..." 
                className="search-input"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <div className="search-icon">
                {searchQuery ? (
                  <button className="clear-search" onClick={clearSearch}>
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                    </svg>
                  </button>
                ) : (
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path>
                  </svg>
                )}
              </div>
            </div>
          </div>
          
          <div className="challenges-section">
            <ChallengeList 
              searchQuery={searchQuery}
              filters={filters}
              searchFields={searchFields}
              searchMode={searchMode}
              dateRange={dateRange.enabled ? selectedDateRange : null} // Pass date range
              sortOption={sortOption}
              showSortDropdown={showSortDropdown}
              setShowSortDropdown={setShowSortDropdown}
              handleSortChange={handleSortChange}
              onDataLoad={handleDataLoad}
            />
          </div>
        </div>
        
        {/* <div className="dashboard-grid">
          <div className="dashboard-item calendar-container">
            <h2 className="section-title">Daily Challenges</h2>
            <Calendar />
          </div>
          
          <div className="dashboard-item leaderboard-container">
            <h2 className="section-title">Leaderboard</h2>
            <Leaderboard data={leaderboardData} />
          </div>
          
          <div className="dashboard-item events-container">
            <h2 className="section-title">Upcoming Events</h2>
            <UpcomingEvents events={upcomingEvents} />
          </div>
        </div>
        
        <div className="mobile-dashboard-sections">
          <h2 className="section-title"></h2>
          <div className="dashboard-item calendar-container">
            <Calendar />
          </div>
          
          <h2 className="section-title"></h2>
          <div className="dashboard-item leaderboard-container">
            <Leaderboard data={leaderboardData} />
          </div>
          
          <h2 className="section-title">Upcoming Events</h2>
          <div className="dashboard-item events-container">
            <UpcomingEvents events={upcomingEvents} />
          </div>
        </div>
       */}
       </div>
    </div>
  );
};

export default Dashboard;